import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress, Stack, IconButton, Checkbox, FormControlLabel,
} from "@mui/material";
import { Person,
         CheckCircleOutline, EmailOutlined, PhoneIphone, MarkEmailRead,
         ArrowBack } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import { EMPTY_ADDRESS, AddressFields, useAddressConfirm, useNpiConfirm } from "./addressShared";
import SignaturePad from "./SignaturePad";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// 2026-10-19 fix (real gap, confirmed by direct testing) — the monthly AI
// subscription opt-in checkbox below was shown unconditionally to every
// patient registrant regardless of this flag, so turning the feature off
// via MONTHLY_DIGEST_ENABLED/REACT_APP_MONTHLY_DIGEST_ENABLED (which
// correctly hides the feature everywhere else — Home.js's nav card,
// MonthlyDigest.js's own direct-URL guard) still left registration selling
// a subscription to a feature nobody could ever actually receive. Same
// flag, same convention as those other two call sites.
const DIGEST_ENABLED = process.env.REACT_APP_MONTHLY_DIGEST_ENABLED === "true";

const BRAND = {
  navy: "#00357a",
  navyDark: "#001f4d",
  gold: "#c9a96e",
};

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}
          {required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 14, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

// 2026-10-26 fix (ui-ux-checker finding, real UI sweep) — this used to
// be a RoleSelector rendering the single remaining "Patient" option as
// a bordered, hoverable, click-handling card under an "I AM A…" heading
// that implied a real choice — a dead affordance ever since clinician
// self-registration was made invite-only (2026-10-23, see register()'s
// matching backend block) left only one option. Replaced with a plain,
// non-interactive label — there's no choice left to make, so it
// shouldn't look like one. `role`/`onChange` props are no longer
// needed (every caller still always produces role="patient").
function RoleSelector({ isDark }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      <Person sx={{ fontSize: 18, color: BRAND.navy }} />
      <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: sub }}>
        Patient Registration
      </Typography>
    </Box>
  );
}

// ── Payment-sent confirmation screen ─────────────────────────────────────────
function PaymentSentScreen({ email, phone, checkoutToken, subscriptionOffer, subscriptionOptin, isDark }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "#111827" : "#ffffff";
  const textSub  = isDark ? "#94a3b8" : "#64748b";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
        bgcolor: cardBg,
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />

        {/* Header */}
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
          <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.2)" }} />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}
          >
            <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Check Your Email & Phone
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
            Your login credentials and <strong>Lab Test Copay ($25)</strong> payment link have been sent to:
          </Typography>

          {/* Delivery channels */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.04)",
              border: `1px solid ${isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.12)"}`,
              borderRadius: "10px", px: 2, py: 1.25,
            }}>
              <EmailOutlined sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
                {email}
              </Typography>
            </Box>
            {phone && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.04)",
                border: `1px solid ${isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.12)"}`,
                borderRadius: "10px", px: 2, py: 1.25,
              }}>
                <PhoneIphone sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
                  {phone}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Steps */}
          <Box sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f6f2",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.08)"}`,
            borderRadius: "10px", px: 2.5, py: 2, mb: 3,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
              letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>
              What to do next
            </Typography>
            {[
              "Check your email for your temporary login credentials",
              "Sign in at labarium.com — you'll be prompted to set a new password",
              "Open the payment link (also in your email & text) to complete the Lab Test Copay ($25)",
              "A payment confirmation will be sent once your copay is received",
            ].map((step, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: i < 3 ? 1.25 : 0 }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  bgcolor: BRAND.navy, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12.5, fontWeight: 800, mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.65 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Proove-parity build, Part 9 — a SEPARATE, optional $0.99/mo
              add-on, shown only when the patient opted in at registration
              and the subscription is actually configured. 2026-10-16 fix
              (ui-ux-checker finding) — this card renders ABOVE the "no
              subscriptions" copay copy below, the opposite of what an
              earlier version of this comment claimed; the copay copy
              itself was reworded below so the two no longer contradict
              regardless of render order. The small uppercase overline
              distinguishes this as the separate, self-selected add-on the
              patient opted into, not part of the required copay flow. */}
          {subscriptionOptin && subscriptionOffer && (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
                letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.75 }}>
                Optional add-on you selected
              </Typography>
              <SubscriptionStartCard offer={subscriptionOffer} checkoutToken={checkoutToken} isDark={isDark} />
            </Box>
          )}

          <Typography sx={{ fontSize: 15, color: textSub, textAlign: "center", lineHeight: 1.7, mb: 2.5, mt: subscriptionOptin && subscriptionOffer ? 2.5 : 0 }}>
            One-time Labarium Lab Test Copay and Activation Fee ($25) — no subscription required for your test.
            Don't see the email? Check your spam folder or{" "}
            <Box component="a" href="mailto:info@labarium.com"
              sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              contact us
            </Box>
          </Typography>

          <Button
            fullWidth variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,122,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}
          >
            Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Proove-parity build, Part 3 — self-pay post-registration screens ──────────

function SelfPayNothingToPayScreen({ email, checkoutToken, subscriptionOffer, subscriptionOptin, isDark }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "#111827" : "#ffffff";
  const textSub  = isDark ? "#94a3b8" : "#64748b";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
        bgcolor: cardBg,
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Check Your Email
          </Typography>
        </Box>
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
            Your login credentials have been sent to <strong>{email}</strong>.
            Nothing is owed right now — you can pay for your test(s) anytime from My Payments after signing in.
          </Typography>
          {subscriptionOptin && subscriptionOffer && (
            // 2026-10-16 fix (ui-ux-checker finding) — this screen's own
            // copy just above says "Nothing is owed right now," so an
            // unframed payment button right after it reads as an abrupt,
            // confusing tonal jump. The overline makes clear this is the
            // separate, self-selected add-on, not a contradiction of
            // "nothing owed."
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
                letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.75 }}>
                Optional add-on you selected
              </Typography>
              <SubscriptionStartCard offer={subscriptionOffer} checkoutToken={checkoutToken} isDark={isDark} />
            </Box>
          )}
          <Typography sx={{ fontSize: 14, color: textSub, textAlign: "center", lineHeight: 1.7, mb: 2.5, mt: subscriptionOptin && subscriptionOffer ? 2.5 : 0 }}>
            Don't see the email? Check your spam folder or{" "}
            <Box component="a" href="mailto:info@labarium.com"
              sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              contact us
            </Box>
          </Typography>
          <Button fullWidth variant="outlined" onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,122,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}>
            Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// Proove-parity build, Part 9 — shared "$0.99/mo" start-subscription card,
// reused across whichever of the 3 post-registration screens renders
// (PaymentSentScreen, PanelCheckoutScreen, SelfPayNothingToPayScreen), all
// via the SAME checkout_token the backend minted at registration — only
// shown when the patient actually checked the opt-in box AND the backend
// confirmed STRIPE_PRICE_MONTHLY_SUBSCRIPTION is configured (subscriptionOffer).
function SubscriptionStartCard({ offer, checkoutToken, isDark }) {
  const [state, setState] = useState("idle"); // idle | loading | error | started
  const [error, setError] = useState(null);

  const handleStart = async () => {
    if (state === "loading" || state === "started") return;
    setState("loading");
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/payments/create-checkout-session`, {
        checkout_token: checkoutToken, item_key: offer.item_key,
      });
      setState("started");
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setState("error");
      if (err?.response?.status === 401) {
        // 2026-10-16 fix (ui-ux-checker finding, CONFIRMED real gap,
        // scoped-verifier follow-up) — My Payments has no subscription-
        // start UI at all today (checked MyPayments.js directly), so the
        // earlier copy here pointed to a dead end. A "refresh and check
        // the box again" suggestion is ALSO a dead end — checkoutToken is
        // in-memory only (lost on refresh), and resubmitting registration
        // with the same email just hits the existing-account 409. Until
        // there's a real self-service recovery path (flagged as an open
        // item in the plan tracker), the only honest instruction is to
        // contact support directly.
        setError("Your checkout session has expired. Please email info@labarium.com to start your subscription.");
      } else {
        setError(extractErrorDetail(err, "Could not start checkout — please try again."));
      }
    }
  };

  // 2026-10-16 fix (ui-ux-checker finding) — "started" means the browser
  // is about to navigate away to Stripe Checkout (window.location.href
  // just got set); the earlier version showed a static, washed-out
  // "Start Subscription" label during that wait with no spinner, which
  // reads as broken/stuck. Show the spinner through "loading" AND
  // "started", matching the actual in-flight state the user is in.
  const isBusy = state === "loading" || state === "started";

  return (
    <Box sx={{
      display: "flex", flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 1.5,
      border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)"}`,
      borderRadius: "10px", px: 2, py: 1.5,
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
          {offer.label}
        </Typography>
        {/* 2026-10-16 fix (ui-ux-checker finding) — #94a3b8 on a white
            background is ~2.6:1 contrast, below WCAG AA's 4.5:1 threshold
            for normal-size text, and this line carries actual recurring-
            charge/cancellation terms a user needs to read. #475569 (already
            used elsewhere in this file at similar weight) clears AA. */}
        <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", overflowWrap: "anywhere" }}>
          {/* 2026-10-19 fix — point to My Payments' real self-serve
              cancellation instead of implying email is the only way. */}
          ${(offer.price_cents / 100).toFixed(2)}/month — cancel anytime from My Payments
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1, borderRadius: 2, fontSize: 13, py: 0 }}>{error}</Alert>
        )}
      </Box>
      <Button
        variant="contained"
        // 2026-10-16 fix (ui-ux-checker finding) — mirrors the 2026-10-15
        // fix already applied to PanelCheckoutScreen's own "Pay Now"
        // button (see its comment above): don't disable the actively-busy
        // button itself (MUI's Mui-disabled background washes out the
        // spinner) — handleStart's own `if (state === "loading" ||
        // state === "started") return;` guard already prevents re-entry.
        onClick={handleStart}
        aria-label={isBusy ? "Starting subscription" : `Start ${offer.label}`}
        sx={{
          background: BRAND.navy, borderRadius: "8px", textTransform: "none", fontWeight: 700,
          px: 2.5, py: 1, fontSize: 14, whiteSpace: "nowrap", flexShrink: 0, "&:hover": { bgcolor: BRAND.navyDark },
        }}>
        {isBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Start Subscription"}
      </Button>
    </Box>
  );
}

// Item 15 (2026-10-16, plan decision #6) — "one-step checkout". Shown
// INSTEAD OF the per-panel "Pay Now" buttons + separate SubscriptionStartCard
// below, only when the patient both has ≥1 unpaid panel AND checked the
// subscription opt-in box at registration — the one case this build item
// actually unifies. If the patient didn't opt into the subscription (or no
// panels are available), PanelCheckoutScreen falls back to its original,
// completely unchanged per-item rendering/endpoints (scope boundary from
// this build item's own brief: "don't force everything through the new
// multi-item code when there's nothing to unify").
function CombinedCheckoutCard({ panels, subscriptionOffer, checkoutToken, isDark }) {
  const [selected, setSelected] = useState(() => new Set());
  const [state, setState] = useState("idle"); // idle | loading | error | started
  const [error, setError] = useState(null);

  const toggle = (itemKey) => {
    if (state === "loading" || state === "started") return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });
  };

  const isBusy = state === "loading" || state === "started";

  const handleCheckout = async () => {
    if (isBusy) return;
    setError(null);
    setState("loading");
    try {
      let res;
      if (selected.size === 0) {
        // Nothing but the subscription selected — no panels to unify with,
        // so this is just the existing single-item endpoint/path, same as
        // SubscriptionStartCard above (not a parallel mechanism).
        res = await axios.post(`${API_BASE}/payments/create-checkout-session`, {
          checkout_token: checkoutToken, item_key: subscriptionOffer.item_key,
        });
      } else {
        res = await axios.post(`${API_BASE}/payments/create-multi-checkout-session`, {
          checkout_token: checkoutToken, item_keys: Array.from(selected), include_subscription: true,
        });
      }
      setState("started");
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setState("error");
      if (err?.response?.status === 401) {
        setError("Your checkout session has expired. Please email info@labarium.com to start your subscription.");
      } else {
        setError(extractErrorDetail(err, "Could not start checkout — please try again."));
      }
    }
  };

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {panels.map((p) => (
          <Box key={p.item_key} onClick={() => toggle(p.item_key)} sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)"}`,
            borderRadius: "10px", px: 2, py: 1.5, cursor: isBusy ? "default" : "pointer",
          }}>
            <FormControlLabel
              sx={{ m: 0, flex: 1, minWidth: 0 }}
              control={<Checkbox checked={selected.has(p.item_key)} disabled={isBusy}
                onChange={() => toggle(p.item_key)} onClick={(e) => e.stopPropagation()} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                    {p.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8" }}>
                    ${(p.price_cents / 100).toFixed(2)}
                  </Typography>
                </Box>
              }
            />
          </Box>
        ))}
      </Stack>

      <Box sx={{
        border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)"}`,
        borderRadius: "10px", px: 2, py: 1.5, mb: 2,
      }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
          letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.5 }}>
          Also included — optional add-on you selected
        </Typography>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
          {subscriptionOffer.label}
        </Typography>
        <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569" }}>
          ${(subscriptionOffer.price_cents / 100).toFixed(2)}/month — cancel anytime from My Payments
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>{error}</Alert>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleCheckout}
        disabled={isBusy}
        aria-label={isBusy ? "Starting checkout" : "Checkout"}
        sx={{
          background: BRAND.navy, borderRadius: "8px", textTransform: "none", fontWeight: 700,
          py: 1.2, fontSize: 15, "&:hover": { bgcolor: BRAND.navyDark },
          "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.15)" },
        }}>
        {isBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} />
          : selected.size === 0 ? "Start Subscription" : "Checkout Selected + Subscription"}
      </Button>
    </Box>
  );
}

function PanelCheckoutScreen({ email, panels, checkoutToken, subscriptionOffer, subscriptionOptin, isDark }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "#111827" : "#ffffff";
  const [payingKey, setPayingKey] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  const handlePay = async (itemKey) => {
    if (payingKey !== null) return; // guard re-entry (the active button stays enabled, see sx below)
    setCheckoutError(null);
    setPayingKey(itemKey);
    try {
      const res = await axios.post(`${API_BASE}/payments/create-checkout-session`, {
        checkout_token: checkoutToken, item_key: itemKey,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setPayingKey(null);
      // checkout_token is short-lived (30 min) — the backend's own generic
      // 401 message says "please refresh," which doesn't actually work on
      // this SPA route (refreshing loses the in-memory token/panels
      // entirely, dropping back to a blank registration form). Give an
      // actionable alternative instead of the literal backend copy here.
      if (err?.response?.status === 401) {
        setCheckoutError("Your checkout session has expired. Please sign in and pay from My Payments instead.");
      } else {
        setCheckoutError(extractErrorDetail(err, "Could not start checkout — please try again."));
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
        bgcolor: cardBg,
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center" }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Complete Your Payment
          </Typography>
        </Box>
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 15, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.75, mb: 2.5, textAlign: "center" }}>
            Your login credentials have been sent to <strong>{email}</strong>.
            Select a test below to pay now, or skip and pay later from My Payments.
          </Typography>

          {checkoutError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 14 }}>{checkoutError}</Alert>
          )}

          {/* Item 15 (2026-10-16, plan decision #6) — "one-step checkout".
              Only when the patient ALSO checked the subscription opt-in at
              registration is there anything to unify: render checkboxes +
              ONE combined "Checkout" button that pays the selected panel(s)
              and starts the subscription in a single Stripe redirect.
              Otherwise (no subscription opt-in), fall back to the original,
              completely unchanged per-panel "Pay Now" buttons below — this
              build item never touches that path. */}
          {subscriptionOptin && subscriptionOffer ? (
            <CombinedCheckoutCard panels={panels} subscriptionOffer={subscriptionOffer}
              checkoutToken={checkoutToken} isDark={isDark} />
          ) : (
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {panels.map((p) => (
                <Box key={p.item_key} sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)"}`,
                  borderRadius: "10px", px: 2, py: 1.5,
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                      {p.label}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8" }}>
                      ${(p.price_cents / 100).toFixed(2)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    // 2026-10-15 fix (ui-ux-checker finding) — only the
                    // OTHER buttons are disabled while one is loading; the
                    // active button itself stays enabled (re-entry is
                    // guarded in handlePay instead) so MUI's own
                    // Mui-disabled background override can never wash out
                    // the one button that's actually mid-payment — the
                    // exact thing a user needs to still see clearly.
                    disabled={payingKey !== null && payingKey !== p.item_key}
                    onClick={() => handlePay(p.item_key)}
                    aria-label={payingKey === p.item_key ? "Processing payment" : `Pay for ${p.label}`}
                    sx={{
                      background: BRAND.navy, borderRadius: "8px", textTransform: "none", fontWeight: 700,
                      px: 2.5, py: 1, fontSize: 14, "&:hover": { bgcolor: BRAND.navyDark },
                      "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.15)" },
                    }}>
                    {payingKey === p.item_key ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Pay Now"}
                  </Button>
                </Box>
              ))}
            </Stack>
          )}

          <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", textAlign: "center", lineHeight: 1.6, mb: 2.5, mt: 2.5 }}>
            Don't see the email? Check your spam folder or{" "}
            <Box component="a" href="mailto:info@labarium.com"
              sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              contact us
            </Box>
          </Typography>

          <Button fullWidth variant="outlined" onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,122,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}>
            Pay Later Instead — Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Clinician account-created confirmation screen ─────────────────────────────
function ClinicianAccountCreatedScreen({ email, isDark }) {
  const navigate = useNavigate();
  const cardBg   = isDark ? "#111827" : "#ffffff";
  const textSub  = isDark ? "#94a3b8" : "#64748b";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Box sx={{
        width: { xs: "100%", sm: 480 }, maxWidth: 480,
        borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
        bgcolor: cardBg,
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />

        {/* Header */}
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
          <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
            borderRadius: "50%", border: "1px solid rgba(192,168,140,0.2)" }} />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}
          >
            <MarkEmailRead sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          </motion.div>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
            ACCOUNT CREATED
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
            Check Your Email
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
            Your clinician account has been created. Your login credentials and a
            temporary password have been sent to:
          </Typography>

          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5, mb: 3,
            bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.04)",
            border: `1px solid ${isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.12)"}`,
            borderRadius: "10px", px: 2, py: 1.25,
          }}>
            <EmailOutlined sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b", fontWeight: 600 }}>
              {email}
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{
            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f6f2",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.08)"}`,
            borderRadius: "10px", px: 2.5, py: 2, mb: 3,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
              letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>
              What to do next
            </Typography>
            {[
              "Open the email from Labarium in your inbox",
              "Sign in using your email and the temporary password provided",
              "You'll be prompted to create your own personal password on first login",
              "You now have full access to Labarium clinician decision support",
            ].map((step, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: i < 3 ? 1.25 : 0 }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  bgcolor: BRAND.navy, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12.5, fontWeight: 800, mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.65 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 15, color: textSub, textAlign: "center", lineHeight: 1.7, mb: 2.5 }}>
            Don't see the email? Check your spam or junk folder. Still nothing?{" "}
            <Box component="a" href="mailto:info@labarium.com"
              sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
              Contact us
            </Box>
          </Typography>

          <Button
            fullWidth variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,122,0.2)",
              color: isDark ? "#94a3b8" : "#64748b",
              borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
              "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
            }}
          >
            Go to Sign In
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

// ── Main register page ────────────────────────────────────────────────────────
export default function RegisterPage() {
  const theme       = useTheme();
  const isDark      = theme.palette.mode === "dark";

  const bg      = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg  = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const [form, setForm] = useState({
    role: "patient", first_name: "", last_name: "", email: "",
    phone: "", date_of_birth: "", practice_name: "", npi: "",
    payment_preference: "now",
    // Proove-parity build, Part 3 — a SEPARATE decision from
    // payment_preference (confirmed design): this decides copay-link vs.
    // panel-checkout; payment_preference decides timing within self-pay,
    // and for insurance is purely informational (the copay link sends
    // either way). Defaults to "insurance", matching today's only
    // pre-existing behavior.
    bill_to: "insurance",
    // Proove-parity build, Part 9 — unchecked by default, independent of
    // bill_to/payment_preference (see this checkbox's own render-site
    // comment and the backend's RegisterRequest.subscription_optin).
    subscription_optin: false,
  });
  // Labarium identity plan, Part 2.2 — patients only. Optional (doesn't
  // hard-block registration) but encouraged — see AddressFields' own copy
  // for why — so a self-registration can link back to an identity a lab
  // already created for the same person.
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS });
  // 2026-10-15 fix (explicit user request: "address is mandatory
  // everywhere") — set true on a failed submit attempt, so AddressFields'
  // red-border/required state doesn't show before the person has even
  // tried to submit the form once.
  const [addressTouched, setAddressTouched] = useState(false);
  // Labarium identity plan — second, independent address-confirmation
  // safety net (see addressShared.js's useAddressConfirm docstring).
  const { confirmAddress, dialog: addressConfirmDialog } = useAddressConfirm(API_BASE);
  // NPPES NPI verification (2026-10-16) — same forced-confirm-before-
  // submit pattern as confirmAddress above, clinicians only. See
  // addressShared.js's useNpiConfirm docstring.
  const { confirmNpi, dialog: npiConfirmDialog } = useNpiConfirm(API_BASE);
  const [loading,      setLoading]      = useState(false);
  const [message,      setMessage]      = useState(null);
  const [paymentSent,  setPaymentSent]  = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  // Proove-parity build, Part 3 — self-pay post-registration screens.
  // checkoutPanels/checkoutToken drive the direct-checkout panel-selection
  // screen (self_pay + "now", with still-unpaid panels available);
  // selfPayNothingToPay covers both self_pay+"later" and self_pay+"now"
  // with nothing left to pay — same simple "account created, no payment
  // needed right now" screen either way.
  const [checkoutPanels, setCheckoutPanels] = useState(null);
  const [checkoutToken,  setCheckoutToken]  = useState(null);
  const [selfPayNothingToPay, setSelfPayNothingToPay] = useState(false);
  // Proove-parity build, Part 9 — the backend's own confirmed $0.99/mo
  // offer (null if STRIPE_PRICE_MONTHLY_SUBSCRIPTION isn't configured),
  // carried alongside whichever of the 3 screens above actually renders.
  // subscriptionOptin mirrors what the patient actually checked at submit
  // time (not re-read from form state, which could change if they somehow
  // navigated back) — gates whether the subscription start-card shows at
  // all on the post-registration screen.
  const [subscriptionOffer, setSubscriptionOffer] = useState(null);
  const [subscriptionOptinAtSubmit, setSubscriptionOptinAtSubmit] = useState(false);

  // Order a Test, Part 6 — clinician signature + consent, required at
  // registration (confirmed directly with the user, not deferred to a
  // later step). Consent text is server-controlled — fetched, never
  // hardcoded here — same reasoning as requisition_schema.py keeping the
  // Labarium legal text as one source of truth.
  const signatureRef = useRef(null);
  const [signatureConsent, setSignatureConsent] = useState(false);
  const [consentText, setConsentText] = useState("");
  useEffect(() => {
    // Fixed 2026-10-08 — this used to fetch on every mount regardless of
    // role, so patient registrations (the default role, form.role initial
    // state above) fired this clinician-only request too, for no reason.
    // Gate it to the clinician branch, and fetch once role flips to it.
    if (form.role !== "clinician" || consentText) return;
    axios.get(`${API_BASE}/api/clinician-signature-consent-text`)
      .then((res) => setConsentText(res.data.consent_text || ""))
      .catch(() => setConsentText(""));
  }, [form.role, consentText]);

  const f = (key) => ({ value: form[key], onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })) });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,35,96,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,53,122,0.08)" },
      "& input[type='date']": {
        colorScheme: isDark ? "dark" : "light",
        "&::-webkit-calendar-picker-indicator": { opacity: 0.5, cursor: "pointer", filter: isDark ? "invert(1)" : "none" },
      },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const isPatient = form.role === "patient";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Clinician NPI validation
    if (!isPatient && form.npi && !/^\d{10}$/.test(form.npi.trim()))
      return setMessage({ severity: "error", text: "NPI must be exactly 10 digits." });

    // Patient requires phone for SMS delivery
    if (isPatient && !form.phone.trim())
      return setMessage({ severity: "error", text: "Phone number is required so we can send your credentials and payment link via text." });

    // 2026-10-15 fix (explicit user request: "address is mandatory
    // everywhere") — was previously optional-but-encouraged (Labarium
    // identity plan, Part 2.2). Now required for every patient
    // registration, same as every other address-collecting form in this app.
    if (isPatient && !(address.line1.trim() && address.city.trim() && address.state.trim() && address.zip.trim())) {
      setAddressTouched(true);
      return setMessage({ severity: "error", text: "Please enter your address." });
    }

    // Order a Test, Part 6 — signature + consent required for clinicians,
    // matching the backend's own required validation exactly (defense in
    // depth, never trusted client-side alone).
    let signaturePngBase64 = null;
    if (!isPatient) {
      signaturePngBase64 = signatureRef.current?.getPngBase64();
      if (!signaturePngBase64)
        return setMessage({ severity: "error", text: "Please draw your signature before creating your account." });
      if (!signatureConsent)
        return setMessage({ severity: "error", text: "Please consent to using your signature on test order requisitions." });
    }

    // Labarium identity plan — forced confirm checkpoint, right before
    // submit, using the backend's authoritative Smarty validation (not
    // just the frontend autocomplete guess). 2026-10-15 — address is now
    // always required for a patient by this point, so this always runs.
    let confirmedAddress = address;
    if (isPatient) {
      const result = await confirmAddress(address);
      if (!result) return; // "No, let me edit" — stay on the form
      confirmedAddress = result;
    }

    // NPPES NPI verification — clinicians only, right before submit (same
    // placement as confirmAddress above) so the confirmation can't go
    // stale after an edit. Skips the dialog entirely when NPI is blank
    // (confirmNpi's own no-op guard) — self-registration allows a blank
    // NPI. Never blocks either way; npiRegistryConfirmed just records
    // the person's own click, threaded into the real submit payload
    // below, separate from the server's own recomputed outcome.
    let npiRegistryConfirmed = false;
    if (!isPatient) {
      npiRegistryConfirmed = await confirmNpi(form.npi.trim(), form.first_name.trim(), form.last_name.trim());
    }

    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        email:      form.email.trim().toLowerCase(),
        role:       form.role,
        phone:      form.phone.trim() || undefined,
      };

      if (isPatient) {
        payload.date_of_birth      = form.date_of_birth || undefined;
        payload.payment_preference = form.payment_preference;
        // Proove-parity build, Part 3 — see form state's own comment.
        payload.bill_to = form.bill_to;
        // Proove-parity build, Part 9 — see form state's own comment.
        payload.subscription_optin = form.subscription_optin;
        // 2026-10-15 fix — address is now always required and validated
        // above before this point is ever reached, so it's always sent.
        payload.address_line1 = confirmedAddress.line1.trim();
        payload.address_unit  = confirmedAddress.unit.trim() || undefined;
        payload.address_city  = confirmedAddress.city.trim();
        payload.address_state = confirmedAddress.state.trim();
        payload.address_zip   = confirmedAddress.zip.trim();
      } else {
        payload.practice_name = form.practice_name.trim() || undefined;
        payload.npi           = form.npi.trim() || undefined;
        payload.signature_png_base64 = signaturePngBase64;
        payload.signature_consent    = signatureConsent;
        payload.npi_registry_confirmed = npiRegistryConfirmed;
      }

      const res = await axios.post(`${API_BASE}/register`, payload);

      if (res.data.status === "account_created" && isPatient) {
        // Proove-parity build, Part 9 — captured regardless of which of
        // the 3 screens below ends up rendering; each screen itself gates
        // on subscriptionOptinAtSubmit && subscriptionOffer before showing
        // the start-subscription card, using this SAME checkout_token.
        setSubscriptionOffer(res.data.subscription_offer || null);
        setSubscriptionOptinAtSubmit(!!form.subscription_optin);
        setCheckoutToken(res.data.checkout_token);

        // Proove-parity build, Part 3 — three distinct post-registration
        // screens for a patient, depending on what actually needs to
        // happen next. Branches on the server's own authoritative
        // res.data.bill_to (2026-10-15 fix, downstream-mismatch-checker
        // finding) — not form.bill_to, the client's own pre-submit guess —
        // since the backend normalizes/defaults an invalid or missing
        // bill_to server-side and that resolved value is what actually
        // drove which branch ran there.
        if (res.data.bill_to === "insurance") {
          setPaymentSent(true);
        } else if (res.data.available_panels && res.data.available_panels.length > 0) {
          // self_pay + "now", with real unpaid panels — direct-checkout
          // screen, using the short-lived checkout_token (never a raw
          // account id — see the backend's own security fix for why).
          setCheckoutPanels(res.data.available_panels);
        } else {
          // self_pay + "later", OR self_pay + "now" with nothing left
          // unpaid — nothing to collect right now either way.
          setSelfPayNothingToPay(true);
        }
      } else {
        setAccountCreated(true);
      }
    } catch (err) {
      setLoading(false);
      setMessage({ severity: "error", text: extractErrorDetail(err, "Registration failed. Please try again.") });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      {addressConfirmDialog}
      {npiConfirmDialog}

      {/* Background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%",  size: 500, color: isDark ? "rgba(0,53,122,0.18)" : "rgba(0,53,122,0.07)", delay: 0 },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(192,168,140,0.08)" : "rgba(192,168,140,0.12)", delay: 1.5 },
        ].map((b, i) => (
          <motion.div key={i} style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%", background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: 2, delay: b.delay, ease: "easeInOut" }} />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}
        style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.97)",
          backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(201,169,110,0.2)" }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton component={Link} to="/"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <ArrowBack sx={{ fontSize: 20 }} />
              </IconButton>
              <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
              </Box>
            </Box>
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10 }}>
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.34, 1.56, 0.64, 1] }}>

          <AnimatePresence mode="wait">
            {paymentSent ? (
              <PaymentSentScreen
                key="payment-sent"
                email={form.email.trim().toLowerCase()}
                phone={form.phone.trim()}
                checkoutToken={checkoutToken}
                subscriptionOffer={subscriptionOffer}
                subscriptionOptin={subscriptionOptinAtSubmit}
                isDark={isDark}
              />
            ) : checkoutPanels ? (
              <PanelCheckoutScreen
                key="panel-checkout"
                email={form.email.trim().toLowerCase()}
                panels={checkoutPanels}
                checkoutToken={checkoutToken}
                subscriptionOffer={subscriptionOffer}
                subscriptionOptin={subscriptionOptinAtSubmit}
                isDark={isDark}
              />
            ) : selfPayNothingToPay ? (
              <SelfPayNothingToPayScreen
                key="self-pay-nothing-to-pay"
                email={form.email.trim().toLowerCase()}
                checkoutToken={checkoutToken}
                subscriptionOffer={subscriptionOffer}
                subscriptionOptin={subscriptionOptinAtSubmit}
                isDark={isDark}
              />
            ) : accountCreated ? (
              <ClinicianAccountCreatedScreen
                key="account-created"
                email={form.email.trim().toLowerCase()}
                isDark={isDark}
              />
            ) : (
              <motion.div key="register-form" exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}>
                <Box sx={{ width: { xs: "100%", sm: 480 }, maxWidth: 480, borderRadius: 3, overflow: "hidden",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
                  boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
                  bgcolor: cardBg }}>

                  <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />

                  {/* Card header */}
                  <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5, position: "relative", overflow: "hidden" }}>
                    <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                      borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
                    <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
                      borderRadius: "50%", border: "1px solid rgba(192,168,140,0.2)" }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, mb: 1 }}>
                      CREATE ACCOUNT
                    </Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                      Join Labarium
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.55)", mt: 0.5 }}>
                      {!isPatient
                        ? "AI-Powered Decision Support Platform"
                        : form.bill_to === "insurance"
                          ? "Lab Test Copay & Activation Fee ($25) — one-time, no subscription required"
                          : "Pay for your test(s) directly — one-time, no subscription required"}
                    </Typography>
                  </Box>

                  {/* Form */}
                  <Box sx={{ px: 4, py: 4 }} component="form" onSubmit={handleSubmit} noValidate>

                    <AnimatePresence mode="wait">
                      {message && (
                        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                          <Alert severity={message.severity} onClose={() => setMessage(null)}
                            sx={{ mb: 2.5, borderRadius: 2, fontSize: 14.5 }}>
                            {message.text}
                          </Alert>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Role label — see RoleSelector's own 2026-10-26 comment */}
                    <RoleSelector isDark={isDark} />

                    {/* Name row */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Field label="First name" required isDark={isDark}>
                        <TextField hiddenLabel fullWidth required placeholder="Jane" sx={inputSx} {...f("first_name")} />
                      </Field>
                      <Field label="Last name" required isDark={isDark}>
                        <TextField hiddenLabel fullWidth required placeholder="Doe" sx={inputSx} {...f("last_name")} />
                      </Field>
                    </Box>

                    {/* Role-specific fields */}
                    <AnimatePresence mode="wait">
                      {isPatient ? (
                        <motion.div key="patient-fields"
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Field label="Date of birth" required isDark={isDark}>
                            <TextField hiddenLabel type="date" fullWidth required sx={inputSx}
                              slotProps={{ htmlInput: { max: new Date().toISOString().split("T")[0] } }}
                              {...f("date_of_birth")} />
                          </Field>
                          {/* 2026-10-15 fix (explicit user request: "address
                              is mandatory everywhere") — was previously
                              optional-but-encouraged (Labarium identity
                              plan, Part 2.2); now required like every other
                              address-collecting form in this app. */}
                          <Field label="Address" required isDark={isDark}>
                            <AddressFields isDark={isDark} inputSx={inputSx} value={address} onChange={setAddress} showRequired={addressTouched} />
                          </Field>
                        </motion.div>
                      ) : (
                        <motion.div key="clinician-fields"
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Field label="Practice / Organization" required isDark={isDark}>
                            <TextField hiddenLabel fullWidth required placeholder="Labarium Health Systems"
                              sx={inputSx} {...f("practice_name")} />
                          </Field>
                          <Field label="NPI number" required isDark={isDark} hint="10 digits">
                            <TextField hiddenLabel fullWidth required placeholder="1234567890"
                              slotProps={{ htmlInput: { maxLength: 10 } }} sx={inputSx} {...f("npi")} />
                          </Field>

                          {/* Order a Test, Part 6 — signature + consent,
                              required at registration. */}
                          <Field label="Your Signature" required isDark={isDark}
                            hint="Used to sign test order requisitions you submit">
                            <SignaturePad ref={signatureRef} isDark={isDark} />
                          </Field>
                          <FormControlLabel
                            sx={{ alignItems: "flex-start", mt: 0.5, mb: 2.5, ml: 0 }}
                            control={
                              <Checkbox
                                checked={signatureConsent}
                                onChange={(e) => setSignatureConsent(e.target.checked)}
                                sx={{ color: BRAND.navy, mt: -0.5, "&.Mui-checked": { color: BRAND.navy } }}
                              />
                            }
                            label={
                              <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.6 }}>
                                {consentText || "By drawing and submitting your signature, you consent to Labarium using it to sign test order requisitions you submit through your account."}
                              </Typography>
                            }
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <Field label="Email address" required isDark={isDark}>
                      <TextField hiddenLabel type="email" fullWidth required
                        placeholder="you@labarium.com" sx={inputSx} {...f("email")} />
                    </Field>

                    <Field label="Phone number" required={isPatient}
                      hint={isPatient ? (form.bill_to === "insurance" ? "Required — credentials & payment link sent by text" : "Required — credentials sent by text") : "Optional"}
                      isDark={isDark}>
                      <TextField hiddenLabel type="tel" fullWidth required={isPatient}
                        placeholder="+1 555 123 4567" sx={inputSx} {...f("phone")} />
                    </Field>

                    {/* Clinician: auto-password note */}
                    <AnimatePresence>
                      {!isPatient && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                          <Box sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.05)",
                            border: `1px solid ${isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.12)"}`,
                            borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                          }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0, mt: 0.7,
                              "@keyframes gp": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                              animation: "gp 2s infinite" }} />
                            <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", lineHeight: 1.65 }}>
                              A temporary password will be emailed to you automatically. On your first login
                              you'll be prompted to set your own personal password.
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Patient: payment preference + note */}
                    <AnimatePresence>
                      {isPatient && (
                        <motion.div
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}>

                          {/* Proove-parity build, Part 3 — bill-to selector,
                              a SEPARATE decision from the now/later toggle
                              below it. */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
                              How will your test(s) be billed?
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              {[
                                { value: "insurance", label: "Insurance", desc: "Bill my insurance, Medicare, or Medicaid" },
                                { value: "self_pay",  label: "I'll pay myself", desc: "Self-pay for my Proove test(s)" },
                              ].map((opt) => {
                                const selected = form.bill_to === opt.value;
                                return (
                                  <Box key={opt.value} onClick={() => setForm(p => ({ ...p, bill_to: opt.value }))}
                                    sx={{
                                      flex: 1, cursor: "pointer", borderRadius: "10px", px: 2, py: 1.5,
                                      border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)")}`,
                                      bgcolor: selected ? (isDark ? "rgba(0,53,122,0.25)" : "rgba(0,53,122,0.06)") : "transparent",
                                      transition: "all 0.15s",
                                    }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                                      <Box sx={{
                                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                                        border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)")}`,
                                        bgcolor: selected ? BRAND.navy : "transparent",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                      }}>
                                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />}
                                      </Box>
                                      <Typography sx={{ fontSize: 14, fontWeight: 700,
                                        color: selected ? (isDark ? "#fff" : BRAND.navy) : (isDark ? "#94a3b8" : "#475569") }}>
                                        {opt.label}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8", pl: 3 }}>
                                      {opt.desc}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>

                          {/* Payment preference toggle */}
                          <Box sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b", mb: 0.5 }}>
                              {form.bill_to === "insurance"
                                ? "Would you like to pay your Lab Test Copay now or at a later time?"
                                : "Would you like to pay for your test(s) now or at a later time?"}
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8", mb: 1.25 }}>
                              {form.bill_to === "insurance"
                                ? "A secure $25 one-time payment link will be sent to your email and phone regardless of your choice."
                                : "Choosing “now” lets you check out directly after creating your account — no need to wait for an email."}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              {[
                                { value: "now",   label: "I'll pay now",   desc: form.bill_to === "insurance" ? "I'd like to complete my copay right after signing up" : "Take me straight to checkout after signing up" },
                                { value: "later", label: "I'll pay later", desc: form.bill_to === "insurance" ? "I'll complete my copay when it's convenient for me" : "I'll pay when it's convenient for me, from My Payments" },
                              ].map((opt) => {
                                const selected = form.payment_preference === opt.value;
                                return (
                                  <Box key={opt.value} onClick={() => setForm(p => ({ ...p, payment_preference: opt.value }))}
                                    sx={{
                                      flex: 1, cursor: "pointer", borderRadius: "10px", px: 2, py: 1.5,
                                      border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)")}`,
                                      bgcolor: selected ? (isDark ? "rgba(0,53,122,0.25)" : "rgba(0,53,122,0.06)") : "transparent",
                                      transition: "all 0.15s",
                                    }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                                      <Box sx={{
                                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                                        border: `2px solid ${selected ? BRAND.navy : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)")}`,
                                        bgcolor: selected ? BRAND.navy : "transparent",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                      }}>
                                        {selected && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />}
                                      </Box>
                                      <Typography sx={{ fontSize: 14, fontWeight: 700,
                                        color: selected ? (isDark ? "#fff" : BRAND.navy) : (isDark ? "#94a3b8" : "#475569") }}>
                                        {opt.label}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8", pl: 3 }}>
                                      {opt.desc}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>

                          {/* Note */}
                          <Box sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            bgcolor: isDark ? "rgba(0,53,122,0.15)" : "rgba(0,53,122,0.05)",
                            border: `1px solid ${isDark ? "rgba(0,53,122,0.3)" : "rgba(0,53,122,0.12)"}`,
                            borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                          }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0, mt: 0.7,
                              "@keyframes gp": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                              animation: "gp 2s infinite" }} />
                            <Typography sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#475569", lineHeight: 1.65 }}>
                              {form.bill_to === "insurance" ? (
                                <>Your account and login credentials are created immediately.
                                A <strong>Lab Test Copay and Activation Fee ($25)</strong> payment link is also sent to your email and phone.</>
                              ) : (
                                <>Your account and login credentials are created immediately either way.
                                {form.payment_preference === "now"
                                  ? " If you still owe anything, you'll be able to check out directly on the next screen."
                                  : " You can pay for your test(s) anytime afterward from My Payments."}</>
                              )}
                            </Typography>
                          </Box>

                          {/* Proove-parity build, Part 9 — $0.99/mo monthly
                              AI subscription, a SEPARATE, unchecked-by-
                              default, account-level opt-in (never tied to
                              the bill_to/payment_preference choices above —
                              see RegisterRequest.subscription_optin's own
                              backend comment). Its own clearly separate
                              section, deliberately not folded into the
                              billing-method copy above (which specifically
                              describes the one-time copay/panel charges as
                              "no subscriptions") — this is a different,
                              genuinely optional, separate line item.

                              2026-10-19 fix (real gap, confirmed) — gated
                              on DIGEST_ENABLED now: this used to render
                              unconditionally, so turning the feature off
                              via REACT_APP_MONTHLY_DIGEST_ENABLED still
                              let a patient pay $0.99/month for a digest
                              that would never actually be generated or
                              delivered (the feature itself is disabled
                              everywhere else once that flag is false). */}
                          {DIGEST_ENABLED && (
                          <Box sx={{
                            display: "flex", alignItems: "flex-start", gap: 1.25,
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)"}`,
                            borderRadius: "10px", px: 2, py: 1.5, mb: 2.5,
                          }}>
                            <Checkbox
                              checked={form.subscription_optin}
                              onChange={(e) => setForm(p => ({ ...p, subscription_optin: e.target.checked }))}
                              sx={{ p: 0, mt: 0.2, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)",
                                "&.Mui-checked": { color: BRAND.navy } }}
                              inputProps={{ "aria-label": "Add Monthly AI Health Insights subscription for $0.99 per month" }}
                            />
                            <Box onClick={() => setForm(p => ({ ...p, subscription_optin: !p.subscription_optin }))} sx={{ cursor: "pointer" }}>
                              {/* 2026-10-16 fix (ui-ux-checker finding) —
                                  "Optional:" now sits in the headline at the
                                  same visual weight as the question itself,
                                  not just in the lighter sub-line below —
                                  a user skimming headlines only would
                                  otherwise miss that this is skippable. */}
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                                Optional: AI-powered monthly health insights from your lab history?
                              </Typography>
                              <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.6 }}>
                                {/* 2026-10-19 fix (real gap, confirmed) —
                                    "contacting info@labarium.com" implied
                                    the only way to cancel was emailing
                                    support; My Payments already has real
                                    self-serve subscription cancellation
                                    (MyPayments.js), so point there instead. */}
                                Add for $0.99/month, cancel anytime from{" "}
                                <Box component="span" sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600 }}>
                                  My Payments
                                </Box>.{" "}
                                {/* 2026-10-16 fix (ui-ux-checker finding,
                                    CONFIRMED real gap) — this used to
                                    unconditionally promise a next-screen
                                    start button even when the backend never
                                    configured the subscription price, which
                                    would silently never appear. Softened to
                                    not guarantee something the backend
                                    response might not actually include. */}
                                If available, you'll be able to start this on the next screen.
                              </Typography>
                            </Box>
                          </Box>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {isPatient && (
                      <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.65, mb: 2, textAlign: "center" }}>
                        By creating your account, you agree to receive transactional text messages and emails from
                        Labarium{form.bill_to === "insurance" ? ", including your payment link ($25 one-time copay) and account notifications" : " about your account and any payments you make"}.
                        Reply <strong>STOP</strong> to any text to opt out of SMS at any time.
                        To opt out of emails, contact{" "}
                        <Box component="a" href="mailto:info@labarium.com"
                          sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600 }}>
                          info@labarium.com
                        </Box>.
                      </Typography>
                    )}

                    <Button type="submit" fullWidth variant="contained" disabled={loading}
                      sx={{
                        mt: 0.5, mb: 2,
                        background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
                        borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                        boxShadow: "0 4px 14px rgba(0,53,122,0.35)",
                        "&:hover": { boxShadow: "0 6px 20px rgba(0,53,122,0.45)", bgcolor: BRAND.navyDark },
                      }}>
                      {loading
                        ? <CircularProgress size={24} sx={{ color: "#fff" }} />
                        : isPatient ? "Create Account →" : "Create Account"}
                    </Button>

                    <Typography sx={{ textAlign: "center", fontSize: 15, color: textSub }}>
                      Already have an account?{" "}
                      <Box component={Link} to="/"
                        sx={{ color: BRAND.gold, textDecoration: "underline", fontWeight: 600 }}>
                        Sign in
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          <Typography sx={{ mt: 2.5, fontSize: 14.5, color: isDark ? "#334155" : "#9ca3af", textAlign: "center" }}>
            AI analysis does not constitute medical advice. Always consult a licensed provider.
          </Typography>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark,
        backdropFilter: "blur(10px)", py: 2, textAlign: "center",
        position: "relative", zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>
          © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. ALL RIGHTS RESERVED.
        </Typography>
      </Box>
    </Box>
  );
}
